/**
 * Generic "source file summary" card: file icon, name, size/row-count summary,
 * an optional sheet picker (multi-sheet workbooks only), and an optional
 * trailing `noteSlot` for feature-specific extra content (e.g. a header-row
 * auto-detection note). Shared by any importer-style feature that needs to
 * show "which file, how big, which sheet" above an editable mapping UI —
 * extracted here (rather than left in one feature and imported by another)
 * per the cross-feature import ban documented in each importer's own spec.
 */
import { FileSpreadsheet } from 'lucide-react';
import { Card, CardContent } from './card';
import { SearchableSelectField } from './searchable-select-field';
import { formatTemplate } from '../lib/utils';

export interface SourceFileSummaryBarLabels {
  /** Template with `{sizeKb}` / `{rows}` tokens, e.g. "{sizeKb} KB · ~{rows} wierszy" */
  fileSummaryTemplate: string;
  /** Template with a `{rows}` token only, used when the file size is unknown, e.g. "~{rows} wierszy" */
  fileSummaryTemplateRowsOnly: string;
  /** e.g. "Arkusz" */
  sheetPickerLabel: string;
}

export interface SourceFileSummaryBarProps {
  fileName: string;
  /** File size in KB. Omit when unknown — the size segment is dropped from the summary. */
  fileSizeKb?: number;
  estimatedRows: number;
  /** All sheet names in the workbook. The picker only renders when length > 1. */
  sheets: string[];
  selectedSheet: string;
  onSheetChange: (sheet: string) => void;
  labels: SourceFileSummaryBarLabels;
  /** Feature-specific trailing content (e.g. a header-row note + InfoHint). */
  noteSlot?: React.ReactNode;
  testId?: string;
}

export function SourceFileSummaryBar({
  fileName,
  fileSizeKb,
  estimatedRows,
  sheets,
  selectedSheet,
  onSheetChange,
  labels,
  noteSlot,
  testId = 'source-file-summary-bar',
}: SourceFileSummaryBarProps) {
  const hasMultipleSheets = sheets.length > 1;
  const sheetOptions = sheets.map((s) => ({ value: s, label: s }));
  const fileSummaryText =
    fileSizeKb != null
      ? formatTemplate(labels.fileSummaryTemplate, { sizeKb: fileSizeKb, rows: estimatedRows })
      : formatTemplate(labels.fileSummaryTemplateRowsOnly, { rows: estimatedRows });

  return (
    <Card data-testid={testId}>
      <CardContent className="p-4 flex items-center gap-6 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex items-center justify-center w-9 h-9 rounded-md bg-muted text-muted-foreground shrink-0">
            <FileSpreadsheet className="h-5 w-5" strokeWidth={1.8} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium truncate" data-testid="source-file-name">
              {fileName}
            </p>
            <p className="text-xs text-muted-foreground">{fileSummaryText}</p>
          </div>
        </div>

        {hasMultipleSheets ? (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{labels.sheetPickerLabel}</span>
            <div className="w-44">
              <SearchableSelectField
                options={sheetOptions}
                value={selectedSheet}
                onChange={onSheetChange}
                data-testid="sheet-picker"
              />
            </div>
          </div>
        ) : null}

        {noteSlot}
      </CardContent>
    </Card>
  );
}
